import { Injectable, OnDestroy } from '@angular/core';
import { AssetManager } from '@app/asset-manager/asset-manager';
import { GaugingElement } from '@app/platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { AssetInfoViewService } from '@app/template/asset-info-view/asset-info-view/asset-info-view.service';
import { Subscription } from 'rxjs';
import * as THREE from '@phdsoft/three';
import { AnomalyModel } from 'projects/phdsoft/common/src/lib/models/anomaly.model';
import { 
  GeometryLabelData, 
  Label2DInfo, 
  createSimpleGeometryCenterLabels2DObjects,
  disposeGroupLinesAndLabels,
  updateLabel2DScales
} from '@app/digital-twin/graphics/labels-utils';
import { InspectionEventsService, InspectionEvent } from './inspection-events.service';
import { StructureTypesEventsService } from './structure-types-events.service';
import { ReferenceDateEventsService, ReferenceDateEvent } from './reference-date-events.service';
import { ElementType } from '@app/platform/busines-data/Framework/Region';
import { GraphicsMaterials } from '@app/digital-twin/graphics/graphics-materials';

@Injectable({
  providedIn: 'root'
})
export class ViewModeAnomalyLabelsService implements OnDestroy {

  private regionChangeSubscription: Subscription;
  private inspectionEventsSubscription: Subscription;
  private structureTypesChangeSubscription: Subscription;
  private referenceDateChangeSubscription: Subscription;
  private currentRegionIds: string[] = [];
  private currentSelectedElementTypes: ElementType[] = [];
  private labelsGroup: THREE.Group;
  private labelInfos: Label2DInfo[] = [];
  private isActive: boolean = false;
  private labelsHiddenByUser: boolean = false;
  private regionsAndSectionsSubscription: Subscription;

  constructor(
    private assetManager: AssetManager,
    private assetInfoViewService: AssetInfoViewService,
    private inspectionEventsService: InspectionEventsService,
    private structureTypesEventsService: StructureTypesEventsService,
    private referenceDateEventsService: ReferenceDateEventsService
  ) {
    this.labelsGroup = new THREE.Group();
    this.labelsGroup.name = 'AnomalyLabelsGroup';


    this.regionChangeSubscription = this.assetInfoViewService.castCurrentRegionIds.subscribe((regionIds: string[]) => {
      this.handleRegionChange(this.normalizeRegionIds(regionIds));
    });

    this.regionsAndSectionsSubscription = this.assetInfoViewService.castRegionName.subscribe(() => {
      this.scheduleSyncWithVisibleAnomalies();
    });

    this.inspectionEventsSubscription = this.inspectionEventsService.inspectionEvents$.subscribe((event: InspectionEvent | null) => {
      if (event) {
        this.handleInspectionEvent(event);
      }
    });


    this.structureTypesChangeSubscription = this.structureTypesEventsService.structureTypesChange$.subscribe((selectedTypes: ElementType[]) => {
      this.currentSelectedElementTypes = selectedTypes;
      this.handleStructureTypesChange(selectedTypes);
    });


    this.referenceDateChangeSubscription = this.referenceDateEventsService.referenceDateChange$.subscribe((event: ReferenceDateEvent | null) => {
      if (event) {
        this.handleReferenceDateChange(event);
      }
    });


    this.setupLabelScaleAutoUpdate();
  }

  setLabelsHiddenByUser(hidden: boolean): void {
    this.labelsHiddenByUser = hidden;
    this.syncWithVisibleAnomalies();
  }

  hasVisibleAnomalies(): boolean {
    return this.getVisibleAnomalies().length > 0;
  }

  syncWithVisibleAnomalies(): void {
    if (this.labelsHiddenByUser) {
      this.deactivateAnomalyLabels();
      return;
    }

    if (this.hasVisibleAnomalies()) {
      this.activateAnomalyLabels();
      return;
    }

    this.deactivateAnomalyLabels();
  }

  private scheduleSyncWithVisibleAnomalies(): void {
    setTimeout(() => {
      this.syncWithVisibleAnomalies();
    }, 100);
  }

  private handleInspectionEvent(event: InspectionEvent): void {
    console.log(`[ViewModeAnomalyLabelsService] Handling ${event.type} event for ${event.elements.length} elements`);
    this.scheduleSyncWithVisibleAnomalies();
  }


  private handleStructureTypesChange(selectedTypes: ElementType[]): void {
    console.log(`[ViewModeAnomalyLabelsService] Structure types changed: ${selectedTypes.map(t => t.name).join(', ')}`);
    this.scheduleSyncWithVisibleAnomalies();
  }


  private handleReferenceDateChange(event: ReferenceDateEvent): void {
    console.log(`[ViewModeAnomalyLabelsService] Reference date changed from ${event.previousDate} to ${event.newDate}`);
    this.scheduleSyncWithVisibleAnomalies();
  }


  activateAnomalyLabels(): void {
    this.isActive = true;
    

    this.currentSelectedElementTypes = this.structureTypesEventsService.getCurrentStructureTypes();
    
    this.refreshAnomalyLabels();
    
    // Add labels group to scene if not already added
    const scene = this.assetManager?.graphics?.scene;
    if (!scene?.children) {
      return;
    }

    // @ts-ignore - Type compatibility issue between THREE.js versions
    if (!scene.children.includes(this.labelsGroup)) {
      // @ts-ignore - Type compatibility issue between THREE.js versions
      scene.add(this.labelsGroup);
    }
  }

  deactivateAnomalyLabels(): void {
    this.isActive = false;
    this.clearLabels();
    this.requestSceneRender();
    
    // Remove labels group from scene
    const scene = this.assetManager?.graphics?.scene;
    if (!scene?.children) {
      return;
    }

    // @ts-ignore - Type compatibility issue between THREE.js versions
    if (scene.children.includes(this.labelsGroup)) {
      // @ts-ignore - Type compatibility issue between THREE.js versions
      scene.remove(this.labelsGroup);
    }
  }


  private handleRegionChange(regionIds: string[]): void {
    this.currentRegionIds = [...regionIds];
    this.scheduleSyncWithVisibleAnomalies();
  }

  private normalizeRegionIds(regionIds: any[]): string[] {
    if (!Array.isArray(regionIds)) {
      return [];
    }

    return [...new Set(regionIds.filter(Boolean))];
  }


  refreshAnomalyLabels(): void {
    if (!this.isActive) {
      return;
    }

    this.clearLabels();
    
    const visibleAnomalies = this.getVisibleAnomalies();
    const geometryLabels = this.createGeometryLabelsFromAnomalies(visibleAnomalies);

    if (geometryLabels.length > 0) {
      this.labelInfos = createSimpleGeometryCenterLabels2DObjects(
        this.labelsGroup,
        geometryLabels,
        this.assetManager.graphics.camera as any
      );
    }

    this.requestSceneRender();
  }


  private getVisibleAnomalies(): AnomalyModel[] {
    if (!this.assetManager.asset?.anomalies || !this.assetManager.asset?.InspectionCampaigns) {
      return [];
    }

    const visibleInspectionElementIds = new Set<string>();
    const selectedElementTypeIds = this.currentSelectedElementTypes.map(type => type.id);
    const shouldFilterByElementType = selectedElementTypeIds.length > 0;
    const currentReferenceDate = this.assetManager.referenceDate;

    this.assetManager.asset.InspectionCampaigns.forEach((campaign) => {
      campaign.Elements.forEach((element) => {
        const gaugingElement = element as GaugingElement;
        const targetTypeId = gaugingElement.targetElement?.TypeId?.toString();
        if (gaugingElement.SurfaceDesign.material == GraphicsMaterials.inspectionMaterial) {
          return;
        }
        const isInCurrentRegion = this.currentRegionIds.length === 0 || this.currentRegionIds.includes(gaugingElement.regionId);
        const isSelectedElementType = !shouldFilterByElementType || selectedElementTypeIds.includes(targetTypeId);

        if (gaugingElement.realVisibility &&
            isInCurrentRegion &&
            targetTypeId &&
            isSelectedElementType) {
          visibleInspectionElementIds.add(gaugingElement.Guid);
        }
      });
    });

    const visibleAnomalies = this.assetManager.asset.anomalies.filter(anomaly => {
      const belongsToVisibleElement = anomaly.inspectionElementId && 
                                    visibleInspectionElementIds.has(anomaly.inspectionElementId);
      
      const shouldHideAnomaly = this.HideAnomalyCompletionDate(anomaly, currentReferenceDate);
      
      return belongsToVisibleElement && !shouldHideAnomaly;
    });

    return visibleAnomalies;
  }


  private HideAnomalyCompletionDate(anomaly: AnomalyModel, referenceDate: Date): boolean {
    const hasDateComplete: boolean = anomaly.dateCompleted != null;
    
    let dateCompletedIsInThePast: boolean = false;
    if (hasDateComplete) {
      dateCompletedIsInThePast = new Date(anomaly.dateCompleted) <= new Date(referenceDate);
    }

    return (dateCompletedIsInThePast);
  }

  private createGeometryLabelsFromAnomalies(anomalies: AnomalyModel[]): GeometryLabelData[] {
    const geometryLabels: GeometryLabelData[] = [];

    // First, deduplicate anomalies by inspectionElementSubTypeId to avoid follow-up duplicates
    const deduplicatedAnomalies = this.deduplicateAnomaliesBySubType(anomalies);

    // Then group by element as before
    const anomaliesByElement = new Map<string, AnomalyModel[]>();
    
    deduplicatedAnomalies.forEach(anomaly => {
      if (anomaly.inspectionElementId) {
        if (!anomaliesByElement.has(anomaly.inspectionElementId)) {
          anomaliesByElement.set(anomaly.inspectionElementId, []);
        }
        anomaliesByElement.get(anomaly.inspectionElementId)!.push(anomaly);
      }
    });

    anomaliesByElement.forEach((elementAnomalies, elementId) => {
      const position = this.getAnomalyPosition(elementAnomalies[0]);
      if (position) {
        const label = this.formatGroupedAnomalyLabel(elementAnomalies);
        geometryLabels.push({
          position: position,
          label: label
        });
      }
    });

    return geometryLabels;
  }

  /**
   * Deduplicates anomalies by inspectionElementSubTypeId, keeping only the most recent one
   */
  private deduplicateAnomaliesBySubType(anomalies: AnomalyModel[]): AnomalyModel[] {
    const anomaliesBySubType = new Map<string, AnomalyModel[]>();
    
    // Group by inspectionElementSubTypeId
    anomalies.forEach(anomaly => {
      if (anomaly.inspectionElementId && anomaly.inspectionElementSubTypeId) {
        const key = `${anomaly.inspectionElementId}_${anomaly.inspectionElementSubTypeId}`;
        if (!anomaliesBySubType.has(key)) {
          anomaliesBySubType.set(key, []);
        }
        anomaliesBySubType.get(key)!.push(anomaly);
      }
    });

    // For each group, select the most recent anomaly
    const deduplicatedAnomalies: AnomalyModel[] = [];
    anomaliesBySubType.forEach((subTypeAnomalies) => {
      if (subTypeAnomalies.length > 0) {
        // Sort by creation date (most recent first) and take the first one
        const sortedAnomalies = subTypeAnomalies.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB.getTime() - dateA.getTime(); // Most recent first
        });

        deduplicatedAnomalies.push(sortedAnomalies[0]);
      }
    });

    return deduplicatedAnomalies;
  }

  private getAnomalyPosition(anomaly: AnomalyModel): THREE.Vector3 | null {
    if (!anomaly.inspectionElementId) {
      return null;
    }


    for (const campaign of this.assetManager.asset.InspectionCampaigns) {
      for (const element of campaign.Elements) {
        const gaugingElement = element as GaugingElement;
        if (gaugingElement.Guid === anomaly.inspectionElementId) {
                     if (gaugingElement.SurfaceDesign?.geometry) {
             gaugingElement.SurfaceDesign.geometry.computeBoundingBox();
             // @ts-ignore - Type compatibility issue between THREE.js versions
             return gaugingElement.getCenter();
           }
        }
      }
    }

    return null;
  }

  private formatAnomalyLabel(anomaly: AnomalyModel): string {
    let label = anomaly.anomalyTypeName || 'Unknown';
    
    if (anomaly.anomalySelectedAttribute) {
      label += ` (${anomaly.anomalySelectedAttribute})`;
    }

    return label;
  }

  private formatGroupedAnomalyLabel(anomalies: AnomalyModel[]): string {
    if (anomalies.length === 1) {
      return `• ${this.formatAnomalyLabel(anomalies[0])}`;
    }

    const anomalyLabels = anomalies.map(anomaly => `• ${this.formatAnomalyLabel(anomaly)}`);
    return anomalyLabels.join('\n');
  }

  private clearLabels(): void {
    if (this.labelsGroup) {
      disposeGroupLinesAndLabels(this.labelsGroup);
      this.labelInfos = [];
    }
  }

  // Graphics only renders on request. clearLabels() drops the old label <div>s from the DOM at
  // once, while the new CSS2DObjects only enter the DOM on the next labelRenderer.render(); without
  // a render request the labels vanish until the ~1 s safety render (they "blink").
  private requestSceneRender(): void {
    this.assetManager?.graphics?.requestRender();
  }

  updateLabelScales(): void {
    if (this.isActive && this.labelInfos.length > 0 && this.assetManager?.graphics?.camera) {
      updateLabel2DScales(this.labelInfos, this.assetManager.graphics.camera as any);
    }
  }

  private setupLabelScaleAutoUpdate(): void {
    const updateScales = () => {
      if (this.isActive && this.labelInfos.length > 0) {
        this.updateLabelScales();
      }
      requestAnimationFrame(updateScales);
    };
    requestAnimationFrame(updateScales);
  }

  getLabelsGroup(): THREE.Group {
    return this.labelsGroup;
  }

  isAnomalyLabelsActive(): boolean {
    return this.isActive;
  }

  ngOnDestroy(): void {
    if (this.regionChangeSubscription) {
      this.regionChangeSubscription.unsubscribe();
    }

    if (this.regionsAndSectionsSubscription) {
      this.regionsAndSectionsSubscription.unsubscribe();
    }

    if (this.inspectionEventsSubscription) {
      this.inspectionEventsSubscription.unsubscribe();
    }

    if (this.structureTypesChangeSubscription) {
      this.structureTypesChangeSubscription.unsubscribe();
    }

    if (this.referenceDateChangeSubscription) {
      this.referenceDateChangeSubscription.unsubscribe();
    }

    this.clearLabels();
    
    const scene = this.assetManager?.graphics?.scene;
    if (!scene?.children) {
      return;
    }

    // @ts-ignore - Type compatibility issue between THREE.js versions
    if (scene.children.includes(this.labelsGroup)) {
      // @ts-ignore - Type compatibility issue between THREE.js versions
      scene.remove(this.labelsGroup);
    }
  }
} 
